import { useState } from 'react';
import { Pencil, Trash2, ExternalLink, ChevronUp, ChevronDown, FileText } from 'lucide-react';
import { ConfirmModal } from '../ui/ConfirmModal';
import { getBcFixedAssetUrl } from '../../utils/bcLinks';
import type { CompanyCar, CompanyOption, CarSortField, SortDir } from '../../types/companyCars';

interface FleetTabProps {
    cars: CompanyCar[];
    companies: CompanyOption[];
    canEdit: boolean;
    sortField: CarSortField;
    sortDir: SortDir;
    onSort: (field: CarSortField) => void;
    onDelete: (id: number) => Promise<void>;
    onEditCar: (car: CompanyCar) => void;
    onOpenDocuments: (car: CompanyCar) => void;
}

function SortHeader({ label, field, currentField, currentDir, onSort, align = 'left' }: {
    label: string; field: CarSortField; currentField: CarSortField; currentDir: SortDir;
    onSort: (field: CarSortField) => void; align?: 'left' | 'right';
}) {
    const isActive = currentField === field;
    return (
        <th
            onClick={() => onSort(field)}
            className={`py-2 px-2 font-medium text-gray-500 whitespace-nowrap cursor-pointer hover:text-gray-700 select-none ${align === 'right' ? 'text-right' : ''}`}
        >
            <span className={`inline-flex items-center gap-0.5 ${align === 'right' ? 'justify-end' : ''}`}>
                {label}
                <span className="inline-flex flex-col -space-y-1">
                    <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                </span>
            </span>
        </th>
    );
}

function LeasingExpiryBadge({ date }: { date: string | null }) {
    if (!date) return null;
    const expiry = new Date(date);
    const now = new Date();
    const daysLeft = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">Expired</span>;
    if (daysLeft < 30) return <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700">{daysLeft}d</span>;
    if (daysLeft < 90) return <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700">{daysLeft}d</span>;
    return <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">{date.slice(0, 10)}</span>;
}

export default function FleetTab({
    cars, companies, canEdit, sortField, sortDir, onSort, onDelete, onEditCar, onOpenDocuments,
}: FleetTabProps) {
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const deletingCar = cars.find(c => c.Id === deletingId);

    const companyName = (code: string) => companies.find(c => c.CompanyCode === code)?.CompanyName || code;
    const co2Display = (kgPerKm: number | null) => kgPerKm == null ? '—' : Math.round(kgPerKm * 1000);
    const priceDisplay = (val: number | null) => val == null ? '—' : val.toLocaleString('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const sortProps = { currentField: sortField, currentDir: sortDir, onSort };

    return (
        <>
            <div className="overflow-x-auto max-h-[calc(100vh-260px)]">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
                        <tr className="border-b border-gray-200 text-left">
                            <SortHeader label="Co." field="CompanyCode" {...sortProps} />
                            <SortHeader label="Driver" field="DriverName" {...sortProps} />
                            <SortHeader label="Brand" field="BrandName" {...sortProps} />
                            <SortHeader label="Model" field="CarModel" {...sortProps} />
                            <SortHeader label="Plate" field="LicensePlate" {...sortProps} />
                            <SortHeader label="Fixed Asset" field="FixedAssetNo" {...sortProps} />
                            <SortHeader label="Since" field="MatriculationDate" {...sortProps} />
                            <SortHeader label="Fuel" field="FuelLabel" {...sortProps} />
                            <SortHeader label="CO₂ g/km" field="Co2WltpKgPerKm" {...sortProps} align="right" />
                            <SortHeader label="Price" field="PurchasePrice" {...sortProps} align="right" />
                            <SortHeader label="Leasing" field="IsLeasing" {...sortProps} />
                            <SortHeader label="Status" field="IsActive" {...sortProps} />
                            <th className="py-2 px-2 font-medium text-gray-500 whitespace-nowrap" title="Documents on this vehicle and the next expiry">Docs</th>
                            {canEdit && <th className="py-2 px-2 font-medium text-gray-500 whitespace-nowrap w-20">Actions</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {cars.map(car => {
                            const bcUrl = car.FixedAssetNo ? getBcFixedAssetUrl(car.CompanyCode, car.FixedAssetNo) : null;
                            return (
                                <tr key={car.Id} className={`hover:bg-gray-50 ${canEdit ? 'cursor-pointer' : ''} ${!car.IsActive ? 'opacity-50' : ''}`}
                                    onClick={() => { if (canEdit) onEditCar(car); }}>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        <span className="text-xs font-medium text-gray-500" title={companyName(car.CompanyCode)}>
                                            {car.CompanyCode}
                                        </span>
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        <span className="font-medium text-gray-800">{car.DriverName || '—'}</span>
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-gray-700">{car.BrandName || '—'}</td>
                                    <td className="py-1.5 px-2 max-w-[200px] truncate text-gray-700" title={car.CarModel || ''}>{car.CarModel || '—'}</td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-gray-700">{car.LicensePlate || '—'}</td>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        {car.FixedAssetNo ? (
                                            bcUrl ? (
                                                <a href={bcUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                                    {car.FixedAssetNo}<ExternalLink className="w-3 h-3" />
                                                </a>
                                            ) : <span className="text-gray-700">{car.FixedAssetNo}</span>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-gray-500 text-xs">
                                        {car.MatriculationDate?.slice(0, 10) || '—'}
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-gray-700">{car.FuelLabel || '—'}</td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-right text-gray-700">{co2Display(car.Co2WltpKgPerKm)}</td>
                                    <td className="py-1.5 px-2 whitespace-nowrap text-right text-gray-700">{priceDisplay(car.PurchasePrice)}</td>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        {car.IsLeasing ? (
                                            <div>
                                                <LeasingExpiryBadge date={car.LeasingExpiryDate} />
                                                {car.LeasingRate != null && <span className="ml-1 text-xs text-gray-400">{car.LeasingRate}/mo</span>}
                                            </div>
                                        ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        {car.IsActive ? (
                                            <span className="px-1.5 py-0.5 text-xs rounded bg-green-100 text-green-700">Active</span>
                                        ) : (
                                            <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Ended {car.EndOfUse?.slice(0, 10)}</span>
                                        )}
                                    </td>
                                    <td className="py-1.5 px-2 whitespace-nowrap">
                                        {/* Open for viewers too; the modal hides upload and delete without CanEdit. */}
                                        <button
                                            onClick={e => { e.stopPropagation(); onOpenDocuments(car); }}
                                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${car.DocumentCount > 0 ? 'text-blue-700 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}
                                            title={car.DocumentCount > 0 ? `${car.DocumentCount} document(s) on this vehicle` : 'No documents yet'}
                                            aria-label={`Documents of ${car.LicensePlate || 'this car'}`}
                                        >
                                            <FileText className="w-3.5 h-3.5" />
                                            {car.DocumentCount > 0 ? car.DocumentCount : canEdit ? '+' : '—'}
                                        </button>
                                        {car.NextValidUntil && <span className="ml-1"><LeasingExpiryBadge date={car.NextValidUntil} /></span>}
                                    </td>
                                    {canEdit && (
                                        <td className="py-1.5 px-2 whitespace-nowrap">
                                            <div className="flex items-center gap-1">
                                                <button onClick={e => { e.stopPropagation(); onEditCar(car); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                                <button onClick={e => { e.stopPropagation(); setDeletingId(car.Id); }} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {cars.length === 0 && (
                            <tr><td colSpan={canEdit ? 14 : 13} className="py-8 text-center text-gray-400">No cars found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {deletingId != null && (
                <ConfirmModal
                    title="Delete Car"
                    message={`Are you sure you want to delete ${deletingCar?.BrandName || ''} ${deletingCar?.CarModel || ''} (${deletingCar?.LicensePlate || 'no plate'})?`}
                    variant="danger"
                    confirmLabel="Delete"
                    onConfirm={async () => { await onDelete(deletingId); setDeletingId(null); }}
                    onCancel={() => setDeletingId(null)}
                />
            )}
        </>
    );
}
